<script lang="ts">import { goto } from "$app/navigation";
import { getSections } from "../data/casino";
import { Card } from "@infinitro/card";
import { favoriteGames, recentlyPlayedGames } from "@infinitro/catalog-ex";
import { DEFAULT_PAGE_SIZE, GameCategory, GameSort, getGames, stateCatalogVersion, toGameSlug, toProviderSlug } from "@infinitro/catalog-min";
import { cfgToThemeCdnPath, stateFeatureSet, stateThemeSetting, stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { Dropdown } from "@infinitro/dropdown";
import { closeThisModal, isIOS, loggedIn, ModalDialog, providerBasePath, searchModalPreselectedTab, stateOpenModal, stateShowSidebarMobile, toCamelCase, toSiteUrl } from "@infinitro/global";
import { SearchIcon } from "@infinitro/icons";
import { Input } from "@infinitro/input";
import { Modal } from "@infinitro/modal";
import { ProviderCard } from "@infinitro/provider-card";
import { Tabs } from "@infinitro/tabs";
import { Text } from "@infinitro/text";
import { onDestroy, onMount } from "svelte";
import { get } from "svelte/store";
import SidebarItem from "./sidebar-item.svelte";
export const thisModal = ModalDialog.Search;
const closeModal = () => {
	closeThisModal(thisModal);
	searchModalPreselectedTab.set(undefined);
};
let gameCategory = $state(GameCategory.All);
let gameCategoryLabel = $state($t(Verbiage.all_games));
let searchQuery = $state("");
let providers = $state([]);
let games = $state([]);
let selectedSortBy = $state(GameSort.Popular);
let selectedProvider = $state("");
let isLoading = $state(true);
let currentPage = $state(1);
let pageSize = DEFAULT_PAGE_SIZE;
let isTransitioning = false;
let recently = [];
let favorites = [];
let initialized = $state(false);
let loadGamesRequestId = 0;
recentlyPlayedGames.subscribe((value) => recently = value);
favoriteGames.subscribe((value) => favorites = value);
const TRANSITION_DURATION = 300;
const normalizeProviderSlug = (slug) => slug.toLowerCase().trim();
const sortProvidersByThemeOrder = (items, topProvidersSlugs) => {
	if (!topProvidersSlugs?.length) return items;
	const orderIndex = new Map(topProvidersSlugs.map((providerSlug, index) => [normalizeProviderSlug(providerSlug), index]));
	return [...items].sort((a, b) => {
		const aSlug = normalizeProviderSlug(toProviderSlug(a.prov || a.name));
		const bSlug = normalizeProviderSlug(toProviderSlug(b.prov || b.name));
		const aOrder = orderIndex.get(aSlug);
		const bOrder = orderIndex.get(bSlug);
		if (aOrder === undefined && bOrder === undefined) return 0;
		if (aOrder === undefined) return 1;
		if (bOrder === undefined) return -1;
		return aOrder - bOrder;
	});
};
const sortProviderGamesByThemeOrder = (items, topProvidersSlugs) => {
	if (!topProvidersSlugs?.length) return items;
	const orderIndex = new Map(topProvidersSlugs.map((providerSlug, index) => [normalizeProviderSlug(providerSlug), index]));
	return [...items].sort((a, b) => {
		const aSlug = normalizeProviderSlug(toProviderSlug(a.prov ?? ""));
		const bSlug = normalizeProviderSlug(toProviderSlug(b.prov ?? ""));
		const aOrder = orderIndex.get(aSlug);
		const bOrder = orderIndex.get(bSlug);
		if (aOrder === undefined && bOrder === undefined) return 0;
		if (aOrder === undefined) return 1;
		if (bOrder === undefined) return -1;
		return aOrder - bOrder;
	});
};
const topProvidersSlugsFromTheme = () => get(stateThemeSetting).topProvidersSlugs;
let hasMore = $state(true);
let loadingMore = $state(false);
let touchStartY = 0;
let gamesContainer = $state(undefined);
let providerOptions = $derived([{
	value: "All",
	label: $t(Verbiage.all_providers)
}, ...providers.map((provider) => ({
	value: provider.name,
	label: provider.name
}))]);
const sortByOptions = [
	{
		label: $t(Verbiage.alphabetical_a_z),
		value: GameSort.Alphabetical
	},
	{
		label: $t(Verbiage.alphabetical_z_a),
		value: GameSort.AlphabeticalZA
	},
	{
		label: $t(Verbiage.popularity),
		value: GameSort.Popular
	},
	{
		label: $t(Verbiage.recently_added),
		value: GameSort.Newest
	}
];
function debounce(func, delay) {
	let timeout;
	return (...args) => {
		clearTimeout(timeout);
		timeout = setTimeout(() => {
			func(...args);
		}, delay);
	};
}
let sections = $derived(getSections($stateFeatureSet, $loggedIn, true));
let observer;
let providerDropdownExtraProps = $derived({
	enableSearch: true,
	searchPlaceholder: `${$t(Verbiage.search)} ${$t(Verbiage.providers)}`
});
function setupObserver() {
	// Cleanup existing observer if any
	if (observer) {
		observer.disconnect();
	}
	// Wait until next frame to ensure DOM is updated
	setTimeout(() => {
		// Find and observe the sentinel
		const sentinel = document.getElementById("search-sentinel");
		if (!sentinel) {
			console.log("Sentinel element not found");
			return;
		}
		observer = new IntersectionObserver((entries) => {
			if (entries[0].isIntersecting) {
				if (!loadingMore && hasMore && ![GameCategory.Favorites, GameCategory.RecentlyPlayed].includes(gameCategory)) {
					debouncedLoadMoreGames();
				}
			}
		}, {
			root: gamesContainer || null,
			rootMargin: "100px",
			threshold: .1
		});
		observer.observe(sentinel);
		console.log("Observer setup complete, observing sentinel");
	}, 300);
}
// Add an explicit event listener for scroll on the gamesContainer
function setupScrollListener() {
	if (gamesContainer) {
		gamesContainer.removeEventListener("scroll", handleScroll);
		gamesContainer.addEventListener("scroll", handleScroll);
		gamesContainer.removeEventListener("touchstart", saveTouchStart);
		gamesContainer.removeEventListener("touchmove", blockIOSRubberBand);
		gamesContainer.addEventListener("touchstart", saveTouchStart, { passive: true });
		gamesContainer.addEventListener("touchmove", blockIOSRubberBand, { passive: false });
		console.log("Scroll listener added to gamesContainer");
	}
}
function saveTouchStart(event) {
	const touch = event.touches[0];
	if (!touch) return;
	touchStartY = touch.clientY;
}
function blockIOSRubberBand(event) {
	if (!$isIOS || !gamesContainer) return;
	const touch = event.touches[0];
	if (!touch) return;
	const currentY = touch.clientY;
	const isPullingDown = currentY > touchStartY;
	const isPushingUp = currentY < touchStartY;
	const atTop = gamesContainer.scrollTop <= 0;
	const atBottom = gamesContainer.scrollTop + gamesContainer.clientHeight >= gamesContainer.scrollHeight - 1;
	// Prevent iOS rubber-band from chaining scroll to the page.
	if (atTop && isPullingDown || atBottom && isPushingUp) {
		event.preventDefault();
	}
}
const selectSection = async (section) => {
	if (section === gameCategory || isTransitioning) return;
	isTransitioning = true;
	isLoading = true;
	if (gamesContainer) {
		gamesContainer.style.opacity = "0";
	}
	await new Promise((resolve) => setTimeout(resolve, TRANSITION_DURATION / 2));
	console.log("selectSection", section);
	if (section === GameCategory.Providers) {
		console.log(`selected providers`);
	}
	const sectionItem = sections.find((s) => s.href === section);
	gameCategory = section;
	if (sectionItem) gameCategoryLabel = $t(sectionItem.name);
	resetGames(section);
	try {
		await loadGames();
	} finally {
		if (gamesContainer) {
			gamesContainer.style.opacity = "1";
		}
		setTimeout(() => {
			isTransitioning = false;
			isLoading = false;
		}, TRANSITION_DURATION / 2);
	}
};
function resetGames(section) {
	currentPage = 1;
	selectedProvider = "";
	games = [];
	hasMore = ![GameCategory.Providers, GameCategory.Originals].includes(section);
}
async function loadMoreGames() {
	if (!hasMore || loadingMore || [GameCategory.Favorites, GameCategory.RecentlyPlayed].includes(gameCategory)) {
		return;
	}
	console.log("Loading more games...", {
		currentPage,
		hasMore,
		loadingMore
	});
	loadingMore = true;
	try {
		let [newGames, , moreGames] = await getGames(currentPage + 1, pageSize, selectedProvider, gameCategory, selectedSortBy, searchQuery);
		if (newGames.length > 0) {
			games = [...games, ...newGames];
			if (gameCategory === GameCategory.Providers) {
				const topSlugs = topProvidersSlugsFromTheme();
				games = sortProviderGamesByThemeOrder(games, topSlugs);
			}
			currentPage++;
		}
		hasMore = moreGames;
		console.log("Loaded more games:", {
			newGamesCount: newGames.length,
			hasMore
		});
	} catch (error) {
		console.warn("Error loading more games:", error);
	} finally {
		loadingMore = false;
	}
}
const debouncedLoadMoreGames = debounce(loadMoreGames, 300);
const loadGames = async () => {
	const requestId = ++loadGamesRequestId;
	isLoading = true;
	try {
		if (gameCategory === GameCategory.Favorites) {
			if (requestId !== loadGamesRequestId) return;
			games = favorites || [];
			hasMore = false;
			return;
		} else if (gameCategory === GameCategory.RecentlyPlayed) {
			if (requestId !== loadGamesRequestId) return;
			games = recently || [];
			hasMore = false;
			return;
		} else {
			const [newGames, newProviders, moreGames] = await getGames(1, pageSize * currentPage, selectedProvider, gameCategory, selectedSortBy, searchQuery);
			if (requestId !== loadGamesRequestId) return;
			games = newGames;
			providers = newProviders;
			const topSlugs = topProvidersSlugsFromTheme();
			if (topSlugs?.length) {
				providers = sortProvidersByThemeOrder(providers, topSlugs);
				if (gameCategory === GameCategory.Providers) {
					games = sortProviderGamesByThemeOrder(games, topSlugs);
				}
			}
			hasMore = moreGames;
			providerOptions = [{
				value: "All",
				label: $t(Verbiage.all_providers)
			}, ...providers.map((provider) => ({
				value: provider.name,
				label: provider.name
			}))];
		}
	} catch (error) {
		console.warn("Error loading games:", error);
	} finally {
		if (requestId === loadGamesRequestId) {
			isLoading = false;
		}
	}
};
const debouncedLoadGames = debounce(loadGames, 300);
function handleScroll() {
	if (!gamesContainer) return;
	if (searchQuery) return;
	const { scrollTop, scrollHeight, clientHeight } = gamesContainer;
	if (scrollTop + clientHeight >= scrollHeight - 100) {
		debouncedLoadMoreGames();
	}
}
const gotoByGameInfo = (game) => {
	closeModal();
	stateShowSidebarMobile.set(false);
	const link = toSiteUrl(game.prov === game.game ? `${providerBasePath(game.prov)}${toProviderSlug(game.prov ?? "")}/` : `${providerBasePath(game.prov ?? "")}${toProviderSlug(game.prov ?? "")}/${toGameSlug(game.game)}/`, $loggedIn);
	// eslint-disable-next-line svelte/no-navigation-without-resolve -- dynamic site URL from toSiteUrl
	goto(link);
};
let catalogVersionAtMount = 0;
onMount(() => {
	catalogVersionAtMount = get(stateCatalogVersion);
	debouncedLoadGames();
});
const unsubscribeCatalogVersion = stateCatalogVersion.subscribe((version) => {
	if (version > catalogVersionAtMount) debouncedLoadGames();
});
// Update setupObserver whenever the games list changes
$effect(() => {
	if ($stateOpenModal === thisModal && games) {
		// Wait for DOM update
		setTimeout(() => {
			setupObserver();
			setupScrollListener();
		}, 100);
	}
});
onDestroy(() => {
	unsubscribeCatalogVersion();
	if (gamesContainer) {
		gamesContainer.removeEventListener("scroll", handleScroll);
		gamesContainer.removeEventListener("touchstart", saveTouchStart);
		gamesContainer.removeEventListener("touchmove", blockIOSRubberBand);
	}
	if (observer) {
		observer.disconnect();
	}
});
$effect(() => {
	if (gameCategory) {
		selectedSortBy = gameCategory === GameCategory.NewGames ? GameSort.Newest : gameCategory === GameCategory.Providers ? GameSort.Alphabetical : GameSort.Popular;
	}
});
let filterKey = $derived(`${gameCategory}|${searchQuery}|${selectedProvider}|${selectedSortBy}`);
$effect(() => {
	if ($stateOpenModal !== thisModal || !initialized) return;
	void filterKey;
	currentPage = 1;
	hasMore = true;
	debouncedLoadGames();
});
$effect(() => {
	if ($stateOpenModal === thisModal && !initialized) {
		// Use predefined tab if provided, otherwise default to All
		if ($searchModalPreselectedTab) {
			gameCategory = $searchModalPreselectedTab;
			const sectionItem = sections.find((s) => s.href === $searchModalPreselectedTab);
			gameCategoryLabel = sectionItem ? $t(sectionItem.name) : $t(Verbiage.all_games);
		} else {
			gameCategory = GameCategory.All;
			gameCategoryLabel = $t(Verbiage.all_games);
		}
		initialized = true;
	}
});
$effect(() => {
	if ($stateOpenModal !== thisModal) {
		initialized = false;
	}
});
let useAlternative2 = $derived($stateUi.sidebar.sidebarStyle === "alternative2");
let useAlternative4 = $derived($stateUi.sidebar.sidebarStyle === "alternative4");
let tabSections = $derived(sections.map((section) => ({
	label: $t(section.name),
	value: section.href,
	remoteIcon: section.icon ?? null
})));
function handleTabChange(detail) {
	selectSection(detail.value);
}
function getStableGameKey(game) {
	return `${game.prov ?? game.p ?? ""}-${game.game}`;
}
</script>

{#if $stateOpenModal === thisModal}
	<Modal {closeModal} width="100%" maxWidth="1140px" padding="1rem 0.8rem;">
		<div class="search-modal-content">
			<div
				style="height: -webkit-fill-available;"
				class="search-modal-sidebar"
				class:search-modal-sidebar-alternative2={useAlternative2}
			>
				{#if useAlternative2}
					<Tabs
						tabs={tabSections}
						activeTab={gameCategory}
						vertical={true}
						size="small"
						onChange={handleTabChange}
						align="start"
					/>
				{:else}
					{#each sections as section (section.id ?? section.href)}
						<SidebarItem
							category={section}
							isSearchModal={true}
							activeSection={gameCategory}
							itemIdPrefix="search"
							paddingLeft={useAlternative4 ? '12px' : '0'}
							paddingRight={useAlternative4 ? '12px' : '0'}
							{selectSection}
						/>
					{/each}
				{/if}
			</div>
			<div class="search-games">
				<div class="title-container">
					<Text type="title" titleSize="h5">{$t(Verbiage.search)} {gameCategoryLabel}</Text>
				</div>
				<div class="search-games-header">
					<div class="search-input search-input-providers">
						<Input
							placeholder="{$t(Verbiage.search)} {gameCategoryLabel}"
							icon={SearchIcon}
							bind:value={searchQuery}
						/>
					</div>
					<div class="search-dropdown">
						<Dropdown
							{...providerDropdownExtraProps}
							placeholder={$t(Verbiage.providers)}
							options={providerOptions}
							bind:value={selectedProvider}
							disabled={gameCategory === GameCategory.Providers}
						/>
					</div>

					<div class="search-dropdown">
						<Dropdown
							placeholder={$t(Verbiage.sort_by)}
							options={sortByOptions}
							bind:value={selectedSortBy}
						/>
					</div>
				</div>
				{#if isLoading && !games.length}
					<div class="loading-container">
						<div class="loading-spinner"></div>
						<p>{$t(Verbiage.loading_games)}</p>
					</div>
				{:else}
					<div
						class={gameCategory === GameCategory.Providers
							? 'search-games-container-providers'
							: 'search-games-container'}
						bind:this={gamesContainer}
					>
						{#if games.length > 0}
							<div class="games-grid">
								{#if gameCategory === GameCategory.Providers}
									{#each games as game (getStableGameKey(game))}
										<!-- eslint-disable svelte/no-navigation-without-resolve -- dynamic site URL from toSiteUrl -->
										<a
											id={game.prov}
											href={toSiteUrl(
												game.prov === game.game
													? `${providerBasePath(game.prov)}${toProviderSlug(game.prov ?? '')}/`
													: `${providerBasePath(game.prov ?? '')}${toProviderSlug(game.prov ?? '')}/${toGameSlug(game.game)}/`,
												$loggedIn
											)}
											title={game.prov}
											aria-label={game.prov}
											onclick={(e) => {
												e.preventDefault();
												gotoByGameInfo(game);
											}}
										>
											<ProviderCard
												item={{ name: game.prov ?? '', prov: toProviderSlug(game.prov ?? '') }}
											/>
										</a>
										<!-- eslint-enable svelte/no-navigation-without-resolve -->
									{/each}
								{:else}
									{#each games as game (getStableGameKey(game))}
										<!-- eslint-disable svelte/no-navigation-without-resolve -- dynamic site URL from toSiteUrl -->
										<a
											id={toCamelCase(game.game)}
											onclick={(e) => {
												e.preventDefault();
												gotoByGameInfo(game);
											}}
											href={toSiteUrl(
												game.prov === game.game
													? `${providerBasePath(game.prov)}${toProviderSlug(game.prov ?? '')}/`
													: `${providerBasePath(game.prov ?? '')}${toProviderSlug(game.prov ?? '')}/${toGameSlug(game.game)}/`
											)}
											title={game.game}
											aria-label={game.game}
										>
											<Card item={game} />
										</a>
										<!-- eslint-enable svelte/no-navigation-without-resolve -->
									{/each}
								{/if}
							</div>
							{#if hasMore}
								<div id="search-sentinel" class="sentinel"></div>
							{/if}
							{#if loadingMore}
								<div class="loading-more">
									<div class="loading-spinner"></div>
									<p>{$t(Verbiage.loading_more_games)}</p>
								</div>
							{/if}
						{:else if games.length === 0 && !isLoading}
							<div class="no-results">
								{#if $stateUi.footer?.useEmbeddedShellDecor}
									<div class="no-results-illustration-container">
										<img
											class="no-results-illustration"
											src={cfgToThemeCdnPath('/sidebar/no-games-result-illustration.svg')}
											alt={$t(Verbiage.icon_alt, 'alts')}
										/>
									</div>
									<div class="no-results-text">
										<Text
											type="title"
											titleSize="h4"
											titleWeight="bold"
											textAlign="center"
											fontFamily="main"
											customStyle="line-height: 56px !important; color: rgb(184, 185, 190) !important;"
										>
											{$t(Verbiage.no_games_found_flush_part_1)}
										</Text>
										<Text
											type="body"
											bodyWeight="regular"
											bodySize="large"
											textAlign="center"
											fontFamily="main"
											customStyle="line-height: 20px !important; font-size: 18px !important; color: rgb(134, 136, 145) !important;"
										>
											{$t(Verbiage.no_games_found_flush_part_2)}
										</Text>
									</div>
								{:else}
									{$t(Verbiage.no_games_found)}
								{/if}
							</div>
						{/if}
					</div>
				{/if}
			</div>
		</div>
	</Modal>
{/if}

<style>
	.search-modal-content {
		position: relative;
		display: flex;
		height: 100%;
		align-items: center;
		justify-content: space-between;
		height: calc(100vh - 2rem)
}

	@media (min-width: 1024px) {
		.search-modal-content {
				height: 761px;
				max-height: 70vh;
				min-height: 450px;
				width: 100%;
				max-width: 90vw
		}
	}

	.search-modal-sidebar {
		position: sticky;
		display: none;
		width: 200px;
		flex-direction: column;
		align-items: center;
		gap: 0.25rem;
		border-radius: 16px;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		background-color: var(--color-background-500);
		padding: 1rem
}

	.search-modal-sidebar-alternative2 {
		border-style: none;
		background-color: transparent;
		padding: 0px;
		padding-top: 20px
}

	@media (min-width: 1024px) {
		.search-modal-sidebar {
				display: flex
		}
	}

	.search-games {
		display: flex;
		height: 100%;
		width: 100%;
		flex-direction: column;
		gap: 1rem;
		padding: 1rem;
		padding-left: 0px;
		padding-right: 0px;
		padding-bottom: 0px;
		overflow: hidden
}

	.search-games-header {
		margin-bottom: 1rem;
		display: flex;
		width: 100%;
		flex-direction: column;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		padding-left: 0px;
		padding-right: 0px
}

	@media (min-width: 1024px) {
		.search-games-header {
				flex-direction: row;
				padding-left: 1rem;
				padding-right: 1rem
		}
	}

	.search-input {
		width: 100%
}

	.search-input-providers {
		width: 100%
}

	.search-dropdown {
		width: 100%
}

	@media (min-width: 1024px) {
		.search-input {
				width: 100%
		}
		.search-input-providers {
				width: 50%
		}
		.search-dropdown {
				width: 25%
		}
	}

	.search-games-container,
	.search-games-container-providers {
		height: 100%;
		width: 100%;
		overflow-y: auto;
		overflow-x: hidden;
		height: calc(100% - 120px);
		-webkit-overflow-scrolling: touch;
		overscroll-behavior: contain;
		touch-action: pan-y
}

	.games-grid {
		display: grid;
		gap: 0.75rem;
		padding-left: 0px;
		padding-right: 0px;
		grid-auto-rows: min-content
}

	@media (min-width: 768px) {
		.games-grid {
				padding-right: 0px
		}
	}

	@media (min-width: 1024px) {
		.games-grid {
				padding-left: 1rem;
				padding-right: 1rem;
				padding-top: 0.7rem
		}
	}

	.search-games-container .games-grid {
		grid-template-columns: repeat(3, minmax(0, 1fr))
}

	@media (min-width: 768px) {
		.search-games-container .games-grid {
				grid-template-columns: repeat(4, minmax(0, 1fr))
		}
	}

	@media (min-width: 1024px) {
		.search-games-container .games-grid {
				grid-template-columns: repeat(6, minmax(0, 1fr))
		}
	}

	.search-games-container-providers .games-grid {
		grid-template-columns: repeat(2, minmax(0, 1fr))
}

	@media (min-width: 768px) {
		.search-games-container-providers .games-grid {
				grid-template-columns: repeat(3, minmax(0, 1fr))
		}
	}

	@media (min-width: 1024px) {
		.search-games-container-providers .games-grid {
				grid-template-columns: repeat(5, minmax(0, 1fr))
		}
	}

	.sentinel {
		height: 1rem;
		width: 100%;
		grid-column: 1 / -1
}

	.search-games-container::-webkit-scrollbar,
	.search-games-container-providers::-webkit-scrollbar {
		width: 0px
}

	@media (min-width: 1024px) {
		.search-games-container::-webkit-scrollbar,
		.search-games-container-providers::-webkit-scrollbar {
				width: 0.25rem
		}
	}

	.search-games-container::-webkit-scrollbar-track,
	.search-games-container-providers::-webkit-scrollbar-track {
		border-radius: 0.5rem;
		background-color: var(--color-background-100);
		padding: 0.25rem
}

	.search-games-container::-webkit-scrollbar-thumb,
	.search-games-container-providers::-webkit-scrollbar-thumb {
		border-radius: 0.5rem;
		background-color: var(--color-neutral-900);
		--tw-shadow-color: var(--shadow-neutral);
		--tw-shadow: var(--tw-shadow-colored)
}

	.search-games-container::-webkit-scrollbar-thumb:hover,
	.search-games-container-providers::-webkit-scrollbar-thumb:hover {
		background-color: var(--color-neutral-900)
}

	.title-container {
		padding-left: 1rem;
		padding-right: 1rem
}

	.loading-container {
		display: flex;
		height: 100%;
		width: 100%;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding-top: 2rem;
		padding-bottom: 2rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1))
}

	.loading-more {
		grid-column: 1 / -1;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding-top: 1rem;
		padding-bottom: 1rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1))
}

	.loading-spinner {
		margin-bottom: 1rem;
		height: 2rem;
		width: 2rem
}

	@keyframes spin {

		to {
				transform: rotate(360deg)
		}
}

	.loading-spinner {
		animation: spin 1s linear infinite;
		border-radius: 9999px;
		border-width: 4px;
		border-color: var(--color-neutral-900);
		--tw-border-opacity: 1;
		border-top-color: rgb(255 255 255 / var(--tw-border-opacity, 1))
}

	.no-results {
		grid-column: 1 / -1;
		padding-top: 2rem;
		padding-bottom: 2rem;
		text-align: center;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1))
}

	.no-results-illustration-container {
		display: flex;
		width: 100%;
		align-items: center;
		justify-content: center
}

	.no-results-illustration {
		height: 176.21px;
		width: 176.21px
}

	.no-results-text {
		width: 100%
}
</style>
